import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Stack, Button, TextField, InputAdornment, Chip,
} from '@mui/material';
import { Search, FileDownload } from '@mui/icons-material';
import VATTreatmentEditor from './VATTreatmentEditor';

// VAT chip styling — keep aligned with VATVendorReview.js color codes.
const VAT_LABELS = {
  uk_standard_20: { label: 'VAT 20%',     color: 'success' },
  uk_reduced_5:   { label: 'VAT 5%',      color: 'success' },
  uk_zero_rated:  { label: 'Zero-rated',  color: 'default' },
  uk_exempt:      { label: 'Exempt',      color: 'default' },
  reverse_charge: { label: 'Reverse chg', color: 'warning' },
  outside_scope:  { label: 'Outside scope', color: 'default' },
  unknown:        { label: 'Unknown VAT', color: 'warning' },
};
function vatChipFor(treatment) {
  return VAT_LABELS[treatment] || { label: 'Set VAT', color: 'default' };
}
function fmtNative(amount, currency) {
  if (amount == null || amount === 0) return null;
  return `${currency || ''} ${Number(amount).toFixed(2)}`.trim();
}

// The amount as actually paid, with the rate it was converted at. USD rows are
// skipped — the USD column already says it. A bad ISO code makes Intl throw, so
// fall back to "CODE 1,234.56".
function fmtNativeWithRate(row) {
  const { native_currency: ccy, native_amount: amt, fx_rate: rate } = row;
  if (!ccy || ccy === 'USD' || amt == null) return null;
  let money;
  try {
    money = new Intl.NumberFormat('en-US', {
      style: 'currency', currency: ccy, maximumFractionDigits: 2,
    }).format(amt);
  } catch {
    money = `${ccy} ${Number(amt).toFixed(2)}`;
  }
  // "avg" because currency_monthly.rate is round(avg(daily rate), 5) — a monthly
  // average, NOT the rate this dated payment actually settled at. Saying "@ 1.3334"
  // unqualified would imply a transaction-level rate that never existed.
  return rate && rate !== 1 ? `${money} @ ${Number(rate).toFixed(4)} avg` : money;
}

// Cents, deliberately — see the note in AggregateView: whole-dollar rounding is what
// made $6,000.26 read as a suspiciously round "$6,000".
const formatUSD = (v) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(v || 0);

function exportCsv(rows, filename) {
  // Header keys are the union of fields any row provides (so order-shaped
  // rows include order_id, brand_name, channel, etc., while OpEx/JE rows
  // just get date/label/amount_usd). This keeps CSV self-describing.
  const candidates = [
    'date', 'order_id', 'order_name', 'brand_name', 'channel',
    'store_or_retailer', 'warehouse_code', 'invoice_number',
    'payment_gateway', 'native_currency', 'native_amount', 'fx_rate',
    'bank_account_name', 'bank_memo', 'line_memo', 'je_memo',
    'vat_treatment', 'reclaimable_vat_native',
    'label', 'amount_usd',
  ];
  const header = candidates.filter((h) => rows.some((r) => r[h] != null && r[h] !== ''));
  const lines = [header.join(',')];
  rows.forEach((r) => {
    const fields = header.map((h) => {
      const v = r[h] ?? '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    });
    lines.push(fields.join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Transaction-level drill view.
 *
 * Optional `rowAction(row) => ReactNode` renders an extra column with a
 * per-row action button. Used by Phase 4 to surface a "Reclassify" button
 * on Pending Queue rows.
 */
export default function TransactionView({ data, rowAction, onLoadMore, loadingMore, onReload }) {
  const [q, setQ] = useState('');
  // Per-row optimistic-update map: rowId → updated treatment. Avoids a full
  // refetch on save while still letting the chip reflect the new value.
  const [vatOverrides, setVatOverrides] = useState({});
  const [editor, setEditor] = useState({ open: false, vendor: '', current: '', rowIds: [], isNewRule: false });
  const filtered = useMemo(() => {
    if (!q) return data.rows;
    const ql = q.toLowerCase();
    // Match across order_id / order_name / label / brand / store / bank-memo —
    // accountants search by any of these when verifying against external platforms.
    return data.rows.filter((r) => {
      const hay = [
        r.label, r.order_id, r.order_name, r.brand_name, r.store_or_retailer,
        r.invoice_number, r.payment_gateway, r.bank_account_name, r.bank_memo, r.je_memo, r.line_memo,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(ql);
    });
  }, [data.rows, q]);

  const filteredTotal = useMemo(
    () => filtered.reduce((s, r) => s + (Number(r.amount_usd) || 0), 0),
    [filtered],
  );

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <Button
          size="small"
          startIcon={<FileDownload />}
          onClick={() => exportCsv(filtered, `${(data.title || 'drill').replace(/[^a-z0-9-]+/gi, '_')}.csv`)}
        >
          CSV
        </Button>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {filtered.length} {filtered.length === 1 ? 'transaction' : 'transactions'} • Total {formatUSD(filteredTotal)}
      </Typography>

      <Box sx={{ maxHeight: 'calc(100vh - 320px)', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ position: 'sticky', top: 0, background: '#f5f5f5', zIndex: 1 }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid #ddd' }}>Date</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid #ddd' }}>Description</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '2px solid #ddd' }}>Amount (USD)</th>
              {rowAction && (
                <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '2px solid #ddd', width: 130 }}></th>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              // Meta line 1: brand · channel · region · gateway · native amount
              // Surfaces what an accountant needs to match a row to its external platform.
              const meta1 = [
                r.brand_name,
                r.channel,
                r.store_or_retailer,
                r.warehouse_code ? `3PL ${r.warehouse_code}` : null,
                r.invoice_number ? `Inv ${r.invoice_number}` : null,
                r.payment_gateway || null,
                fmtNativeWithRate(r),
              ].filter(Boolean);

              // Meta line 2 (bank-statement narrative — for cross-checking against
              // the actual bank/card statement). Comes from amex/wise/paypal/revolut
              // source rows for OpEx, or order memos/notes where exposed.
              const bankMemo = r.bank_memo || null;
              const bankAccount = r.bank_account_name || null;
              const jeMemo = r.je_memo && r.je_memo !== r.bank_memo ? r.je_memo : null;
              const lineMemo = r.line_memo && r.line_memo !== r.bank_memo && r.line_memo !== r.je_memo ? r.line_memo : null;

              // Order P&L breakdown (only shown when search-bar enrichment is present).
              // Each component shows native (in customer's currency) for verification
              // against the platform (Shopify Admin / Amazon SC).
              const ccy = r.native_currency || '';
              const fmtNat = (v) => v != null && v !== 0 ? `${ccy} ${Number(v).toFixed(2)}` : null;
              const breakdown = [
                r.cogs_native != null ? `COGS ${fmtNat(r.cogs_native)}` : null,
                r.shipping_native != null ? `Ship ${fmtNat(r.shipping_native)}` : null,
                r.handling_native != null ? `Handling ${fmtNat(r.handling_native)}` : null,
                r.payment_fees_native != null ? `Fees ${fmtNat(r.payment_fees_native)}` : null,
                r.taxes_native != null ? `VAT ${fmtNat(r.taxes_native)}` : null,
                r.refund_native != null && r.refund_native > 0 ? `Refund ${fmtNat(r.refund_native)}` : null,
              ].filter(part => part && !part.endsWith('null'));

              return (
                <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{r.date || ''}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <div>{r.label}</div>
                    {meta1.length > 0 && (
                      <div style={{ fontSize: '11px', color: '#666', marginTop: 2 }}>
                        {meta1.join(' · ')}
                      </div>
                    )}
                    {bankAccount && (
                      <div
                        style={{ fontSize: '11px', color: '#444', marginTop: 2 }}
                        title="Bank account this transaction was paid from"
                      >
                        💳 {bankAccount}
                      </div>
                    )}
                    {(() => {
                      const effectiveTreatment = vatOverrides[r.id] ?? r.vat_treatment;
                      const hasRule = !!(r.vat_rule_name || vatOverrides[r.id]);
                      const hasCandidate = !!r.vendor_candidate;
                      // Show chip when either: (1) we have a rule already, or
                      // (2) we have a vendor candidate to suggest classifying.
                      if (!hasRule && !hasCandidate) return null;
                      const chip = hasRule
                        ? vatChipFor(effectiveTreatment)
                        : { label: 'Set VAT rule', color: 'default' };
                      const reclaim = hasRule
                        ? fmtNative(r.reclaimable_vat_native, r.native_currency)
                        : null;
                      const openEditor = () => setEditor({
                        open: true,
                        vendor: hasRule ? (r.vat_rule_name || '') : (r.vendor_candidate || ''),
                        current: effectiveTreatment || '',
                        rowIds: hasRule
                          ? data.rows.filter((row) => row.vat_rule_name === r.vat_rule_name).map((row) => row.id)
                          : [r.id],
                        isNewRule: !hasRule,
                      });
                      return (
                        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Chip
                            size="small"
                            label={chip.label}
                            color={chip.color}
                            variant={hasRule ? 'filled' : 'outlined'}
                            onClick={openEditor}
                            sx={{ cursor: 'pointer', height: 20, fontSize: 10 }}
                            title={hasRule
                              ? 'Click to change VAT treatment for this vendor (applies to all past + future)'
                              : `Click to create a VAT rule. Suggested vendor: ${r.vendor_candidate}`}
                          />
                          {reclaim && (
                            <Typography variant="caption" color="text.secondary">
                              reclaim {reclaim}
                            </Typography>
                          )}
                        </div>
                      );
                    })()}
                    {bankMemo && (
                      <div
                        style={{ fontSize: '11px', color: '#444', marginTop: 2, fontStyle: 'italic' }}
                        title="Bank statement narrative — cross-check against your actual bank/card statement"
                      >
                        🏦 {bankMemo}
                      </div>
                    )}
                    {breakdown.length > 0 && (
                      <div style={{ fontSize: '11px', color: '#555', marginTop: 2 }}>
                        {breakdown.join(' · ')}
                      </div>
                    )}
                    {jeMemo && (
                      <div style={{ fontSize: '11px', color: '#888', marginTop: 1 }}>
                        JE memo: {jeMemo}
                      </div>
                    )}
                    {lineMemo && (
                      <div style={{ fontSize: '11px', color: '#888', marginTop: 1 }}>
                        Line: {lineMemo}
                      </div>
                    )}
                  </td>
                  <td
                    style={{
                      padding: '6px 8px',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      color: r.amount_usd < 0 ? '#c62828' : 'inherit',
                    }}
                  >
                    {formatUSD(r.amount_usd)}
                  </td>
                  {rowAction && (
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      {rowAction(r)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>

      {data.has_more && onLoadMore && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button onClick={onLoadMore} variant="outlined" size="small" disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Showing {data.rows.length} so far. Use "Download all CSV" for the full dataset.
          </Typography>
        </Box>
      )}

      <VATTreatmentEditor
        open={editor.open}
        vendorName={editor.vendor}
        currentTreatment={editor.current}
        isNewRule={editor.isNewRule}
        onClose={() => setEditor({ ...editor, open: false })}
        onSaved={(newTreatment /* , finalVendor, result */) => {
          // Optimistic update for every visible row sharing this vendor.
          // The refetch below is what actually matters now: saving mints an exact classifier
          // rule and converges the ledger, so the reloaded rows carry a real booked 2210 line
          // and render their treatment + reclaimable amount from that — no memo tag needed.
          setVatOverrides((prev) => {
            const next = { ...prev };
            editor.rowIds.forEach((id) => { next[id] = newTreatment; });
            return next;
          });
          // Hard refetch so reclaimable amounts reflect the new treatment.
          onReload?.();
        }}
      />
    </Box>
  );
}
